import React, { useRef, useEffect, useState } from 'react';
import { Group, Rect, Line } from 'react-konva';
import { getRelativeCoordsAsymmetrical, getAbsoluteCoordsAsymmetrical } from './utils';

const editing_color = '#FF0000';
const size_point = 10;

const generic_point_props = {
	offsetX:size_point / 2,
	offsetY:size_point / 2,
	width:size_point,
	height:size_point,
	draggable:true,
};

const standard_board_size_w = 600;	// px
const standard_board_size_h = 600;	// px

const EditableArea = (props) => {
	let refs = useRef([]);

	const is_editing = props?.is_editing ?? false;
	const size_w = props?.size_w ?? (props?.size || standard_board_size_w);
	const size_h = props?.size_h ?? (props?.size || standard_board_size_h);
	const proportion = props?.proportion ?? false;
	const allow_resize = props?.allow_resize ?? true;

	const default_w = 0.5;
	const default_h = (proportion !== false) ? (default_w * proportion.h / proportion.w) : 0.5;
	const default_area_coords = {
		w:default_w,
		h:default_h,
		x:(1 - default_w) / 2,
		y:(1 - default_h) / 2,
	};
	
	const area = useRef(props?.area ?? default_area_coords);
	
	const [current_pos, setCurrentPos] = useState(area.current);

	const area_abs = useRef(getAbsoluteCoordsAsymmetrical(area.current, size_w, size_h));
	const container_abs = getAbsoluteCoordsAsymmetrical(props?.container || { w:1, h:1 }, size_w, size_h);

	/**
	 * Limit dragging into x-axis
	 */
	const dragX = (pos) => {
		return { x:pos.x, y:area_abs.current.y + area_abs.current.h / 2 }
	};

	/**
	 * Limit dragging into y-axis
	 */
	const dragY = (pos) => {
		return { x:area_abs.current.x + area_abs.current.w / 2, y:pos.y }
	};

	const changesEnded = () => {
		if(props.areaChanged != undefined) props.areaChanged(area.current);
	};

	const changeArea = (point_id) => {
		if(!is_editing) return;

		const moving_point = refs.current[`point_${point_id}`].attrs;

		const min_side_size = 20;

		if(point_id == 'all') {
			area_abs.current.x = Math.max(0, Math.min(moving_point.x, container_abs.w - area_abs.current.w));
			area_abs.current.y = Math.max(0, Math.min(moving_point.y, container_abs.h - area_abs.current.h));
		} else {
			if(proportion !== false) {
				switch(point_id) {
					case 't':
						area_abs.current.y = Math.max(0, Math.min(moving_point.y, refs.current['point_b'].attrs.y - min_side_size));
						area_abs.current.h = refs.current['point_b'].attrs.y - area_abs.current.y;
						break;

					case 'b':
						area_abs.current.h = Math.min(moving_point.y, container_abs.h) - area_abs.current.y;
						break;

					case 'l':
					case 'tl':
					case 'bl':
						area_abs.current.x = Math.max(0, Math.min(moving_point.x, refs.current['point_r'].attrs.x - min_side_size));
						area_abs.current.w = refs.current['point_r'].attrs.x - area_abs.current.x;
						break;
			
					case 'r':
					case 'tr':
					case 'br':
						area_abs.current.w = Math.min(moving_point.x, container_abs.w) - area_abs.current.x;
						break;
				}

				area_abs.current.w = Math.max(area_abs.current.w, min_side_size);
				area_abs.current.h = Math.max(area_abs.current.h, min_side_size);

				let pre_x, pre_w, pre_y, pre_h, w_diff, h_diff;

				switch(point_id) {
					case 't':
					case 'b':
						pre_x = area_abs.current.x;
						pre_w = area_abs.current.w;

						area_abs.current.w = area_abs.current.h * proportion.w / proportion.h;
						w_diff = area_abs.current.w - pre_w;
						
						area_abs.current.x = pre_x - w_diff / 2;
						break;
					
					case 'l':
					case 'r':
					case 'tl':
					case 'tr':
						pre_y = area_abs.current.y;
						pre_h = area_abs.current.h;

						area_abs.current.h = area_abs.current.w * proportion.h / proportion.w;
						h_diff = area_abs.current.h - pre_h;
						
						area_abs.current.y = pre_y - h_diff / ((point_id == 'l' || point_id == 'r') ? 2 : 1);
						break;

					case 'bl':
					case 'br':
						area_abs.current.h = area_abs.current.w * proportion.h / proportion.w;
						break;
				}
			} else {
				if(point_id.indexOf('t') !== -1) {
					area_abs.current.y = Math.max(0, Math.min(moving_point.y, refs.current['point_b'].attrs.y - min_side_size));
					area_abs.current.h = refs.current['point_b'].attrs.y - area_abs.current.y;
				}
	
				if(point_id.indexOf('b') !== -1) {
					area_abs.current.h = Math.min(moving_point.y, container_abs.h) - area_abs.current.y;
				}
	
				if(point_id.indexOf('l') !== -1) {
					area_abs.current.x = Math.max(0, Math.min(moving_point.x, refs.current['point_r'].attrs.x - min_side_size));
					area_abs.current.w = refs.current['point_r'].attrs.x - area_abs.current.x;
				}
	
				if(point_id.indexOf('r') !== -1) {
					area_abs.current.w = Math.min(moving_point.x, container_abs.w) - area_abs.current.x;
				}

				area_abs.current.w = Math.max(area_abs.current.w, min_side_size);
				area_abs.current.h = Math.max(area_abs.current.h, min_side_size);
			}
		}

		const new_area_rel = getRelativeCoordsAsymmetrical(area_abs.current, size_w, size_h);
		if(props.areaMove != undefined) props.areaMove(new_area_rel);
		
		area.current = new_area_rel;
		setCurrentPos(new_area_rel);
		redrawHolders();
	};

	const area_pointers = [
		{
			id:'tl',
			x:area_abs.current.x,
			y:area_abs.current.y,
		}, {
			id:'tr',
			x:area_abs.current.x + area_abs.current.w,
			y:area_abs.current.y,
		}, {
			id:'br',
			x:area_abs.current.x + area_abs.current.w,
			y:area_abs.current.y + area_abs.current.h,
		}, {
			id:'bl',
			x:area_abs.current.x,
			y:area_abs.current.y + area_abs.current.h,
		}, {
			id:'t',
			x:area_abs.current.x + area_abs.current.w / 2,
			y:area_abs.current.y,
			axis:'y',
		}, {
			id:'l',
			x:area_abs.current.x,
			y:area_abs.current.y + area_abs.current.h / 2,
			axis:'x',
		}, {
			id:'b',
			x:area_abs.current.x + area_abs.current.w / 2,
			y:area_abs.current.y + area_abs.current.h,
			axis:'y',
		}, {
			id:'r',
			x:area_abs.current.x + area_abs.current.w,
			y:area_abs.current.y + area_abs.current.h / 2,
			axis:'x',
		},
	];

	const redrawHolders = () => {
		if(refs.current['area_ref'] == undefined) return;

		area_pointers.map((point) => {
			refs.current[`point_${point.id}`]?.setAttrs({
				x:point.x,
				y:point.y,
			});
		});
		
		refs.current[`point_all`]?.setAttrs({
			width:area_abs.current.w,
			height:area_abs.current.h,
			x:area_abs.current.x,
			y:area_abs.current.y,
		});

		refs.current['area_ref'].draw();
	};

	useEffect(() => {
		if(props.area == undefined) {
			area.current = default_area_coords;
			changesEnded();
			return;
		}
		area.current = props.area;
		area_abs.current = getAbsoluteCoordsAsymmetrical(props.area, size_w, size_h);
		if(!props.is_editing || true)redrawHolders();
	}, [props.area]);

	const main_color = props?.guides_color || editing_color;

	return (
		<Group x={props?.x ?? 0} y={props?.y ?? 0} ref={(node) => { refs.current['area_ref'] = node; }}>
			<Rect
				ref={(node) => { refs.current['point_all'] = node; }}
				x={area_abs.current.x}
				y={area_abs.current.y}
				width={area_abs.current.w}
				height={area_abs.current.h}
				strokeWidth={(is_editing) ? 2 : 1}
				dash={[size_point, size_point / 2]}
				dashEnabled={!is_editing}
				stroke={main_color}
				draggable={is_editing}
				onDragMove={() => changeArea('all')}
				onTouchMove={() => changeArea('all')}
				onMouseUp={changesEnded}
			/>

			{(is_editing && allow_resize) && (
				<Group
					x={area_abs.current.x + area_abs.current.w / 2}
					y={area_abs.current.y + area_abs.current.h / 2}
					offsetX={size_point / 2}
					offsetY={size_point / 2}
				>
					<Line points={[ 0, size_point / 2, size_point, size_point / 2 ]} strokeWidth={1} stroke={main_color} />
					<Line points={[ size_point / 2, 0, size_point / 2, size_point ]} strokeWidth={1} stroke={main_color} />
				</Group>
			)}

			{is_editing && allow_resize && area_pointers.map((point) => {
				let point_props = {
					ref:(node) => { refs.current[`point_${point.id}`] = node; },
					x:point.x,
					y:point.y,
					onDragMove:() => changeArea(point.id),
					onTouchMove:() => changeArea(point.id),
					onMouseUp:changesEnded,
				};

				if(point.axis != undefined) {
					point_props.dragBoundFunc = (point.axis == 'y') ? dragY : dragX;
				}
				return (
					<Rect
						key={`point_${point.id}`}
						fill={main_color}
						{...point_props}
						{...generic_point_props}
					/>
				)
			})}
		</Group>
	)
};

export default EditableArea;